import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { trackEvent, trackException } from "@/lib/firebase";

export const CTASection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finease-crm.vercel.app';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackEvent("cta_submit_attempt", {
      service_name: formData.service || "",
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: '',
          service: formData.service,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Submission failed');
      }

      setIsSubmitted(true);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      trackEvent("cta_submitted", {
        service_name: formData.service || "",
      });
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Something went wrong. Please try again.');
      trackEvent("cta_submit_failed", {
        service_name: formData.service || "",
        reason: error?.message || "unknown",
      });
      trackException("cta_submit_request_failed");
    } finally {
      setIsSubmitting(false);
    }

    // Reset form after delay
    setTimeout(() => {
      setFormData({ name: "", email: "", service: "" });
      setIsSubmitted(false);
    }, 3000);
  };

  const services = [
    "Startup Advisory",
    "Business Consulting",
    "CFO Services",
    "Tax & Investments",
    "Other",
  ];

  return (
    <section data-testid="cta-section" className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0 cta-gradient" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00C896]/10 blur-[150px] rounded-full" />

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Get Started
          </span>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Ready to Transform Your Finances?
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Schedule a complimentary consultation with our team. We'll discuss your
            goals and create a tailored strategy for your success.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <form
            onSubmit={handleSubmit}
            data-testid="cta-form"
            className="glass rounded-2xl p-8 md:p-10"
          >
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-8">
                <CheckCircle className="w-16 h-16 text-[#00C896] mb-4" />
                <h3 className="text-2xl font-semibold text-white mb-2">
                  Request Received!
                </h3>
                <p className="text-slate-400">
                  We'll be in touch within 24 hours.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm text-slate-400 mb-2"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    data-testid="cta-input-name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm text-slate-400 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    data-testid="cta-input-email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors"
                    placeholder="john@company.com"
                  />
                </div>

                {/* Service */}
                <div>
                  <label
                    htmlFor="service"
                    className="block text-sm text-slate-400 mb-2"
                  >
                    Service Needed
                  </label>
                  <select
                    id="service"
                    data-testid="cta-select-service"
                    value={formData.service}
                    onChange={(e) =>
                      setFormData({ ...formData, service: e.target.value })
                    }
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-[#0A0A0A]">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service} className="bg-[#0A0A0A]">
                        {service}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {!isSubmitted && (
              <div className="mt-8 flex justify-center">
                <button
                  type="submit"
                  data-testid="cta-submit-btn"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-10 py-4 rounded-full hover:bg-[#00E6AC] transition-colors shadow-[0_0_20px_rgba(0,200,150,0.3)] hover:shadow-[0_0_30px_rgba(0,200,150,0.5)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Request Consultation
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
